import { Head, Link } from '@inertiajs/react'
import { Edit, Plus, Server as ServerIcon, AppWindow, ExternalLink } from 'lucide-react'
import Heading from '@/components/heading'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import AppLayout from '@/layouts/app-layout'
import { serversPath, serverPath, editServerPath, serverAppsPath, newServerAppPath, appPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'
import type { Server, App } from '@/types'

type ServerShowProps = {
  server: Server & { apps: Pick<App, 'id' | 'name' | 'app_url' | 'location'>[] }
}

export default function ServerShow({ server }: ServerShowProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Servers', href: serversPath() },
    { title: server.name, href: serverPath(server.id) },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={server.name} />

      <div className="p-6">
        <div className="flex items-center justify-between">
        <Heading
          title={server.name}
          description={server.hostname || 'Server overview'}
        />
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link href={editServerPath(server.id)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
          <Button asChild>
            <Link href={newServerAppPath(server.id)}>
              <Plus className="mr-2 h-4 w-4" />
              Add App
            </Link>
          </Button>
        </div>
      </div>

      {server.info && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Server Info</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm dark:prose-invert max-w-none">
              {server.info}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Applications</h2>
          <Link 
            href={serverAppsPath(server.id)} 
            className="text-sm text-primary hover:underline"
          >
            View all →
          </Link>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {server.apps.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <AppWindow className="h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No apps yet</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Add your first application to this server
                </p>
                <Button asChild className="mt-4">
                  <Link href={newServerAppPath(server.id)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add App
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            server.apps.map((app) => (
              <Link key={app.id} href={appPath(app.id)}>
                <Card className="group cursor-pointer transition-shadow hover:shadow-md">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AppWindow className="h-5 w-5" />
                      {app.name}
                    </CardTitle>
                    {app.location && (
                      <CardDescription className="truncate">{app.location}</CardDescription>
                    )}
                  </CardHeader>
                  {app.app_url && (
                    <CardContent>
                      <p className="flex items-center gap-1 text-sm text-muted-foreground truncate">
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        {app.app_url}
                      </p>
                    </CardContent>
                  )}
                </Card>
              </Link>
            ))
          )}
        </div>
      </div>
      </div>
    </AppLayout>
  )
}
